import { useState, useEffect, useCallback } from "react";
import { searchService, searchUtils, SearchResult } from "../services/search";
import { useNavigate, useLocation } from "react-router-dom";

export interface DeviceDetails {
  identifier: string;
  title: string;
  recordCount: number;
  rawMetadata: any;
}

export const useSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize state from URL parameters
  const getInitialQuery = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get("q") || "";
  }, [location.search]);
  
  const [searchQuery, setSearchQuery] = useState(getInitialQuery());
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(!!getInitialQuery());
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);

  const parseMetadata = searchUtils.parseMetadata;

  const updateUrlParams = useCallback((query: string) => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    
    const newSearch = params.toString() ? `?${params.toString()}` : "";
    if (location.search !== newSearch) {
      navigate({
        pathname: location.pathname,
        search: newSearch
      }, { replace: false });
    }
  }, [navigate, location.pathname, location.search]);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    setHasSearched(true);
    setLoading(true);
    setSelectedDevice(null);
    
    // Update URL with search query only (no device)
    updateUrlParams(query);

    try {
      if (!query.trim()) {
        setResults([]);
        setLoading(false);
        return;
      }

      const searchResults = await searchService.search(query);

      const sortedResults = [...searchResults].sort((a, b) => {
        const metadataA = parseMetadata(a.metadata_raw);
        const metadataB = parseMetadata(b.metadata_raw);
        const timestampA =
          metadataA.timestamp || metadataA.date || new Date().toISOString();
        const timestampB =
          metadataB.timestamp || metadataB.date || new Date().toISOString();

        return new Date(timestampB).getTime() - new Date(timestampA).getTime();
      });

      setResults(sortedResults);
    } catch (error) {
      console.error("Search error:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };



  // Fetch initial search results when component mounts or URL changes
  useEffect(() => {
    const fetchInitialResults = async () => {
      try {
        setLoading(true);
        const initialResults = await searchService.search("recent");
        setResults(initialResults);
      } catch (error) {
        console.error("Error fetching initial results:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchSearchResults = async (query: string) => {
      try {
        setLoading(true);
        const searchResults = await searchService.search(query);

        const sortedResults = [...searchResults].sort((a, b) => {
          const metadataA = parseMetadata(a.metadata_raw);
          const metadataB = parseMetadata(b.metadata_raw);
          const timestampA =
            metadataA.timestamp || metadataA.date || new Date().toISOString();
          const timestampB =
            metadataB.timestamp || metadataB.date || new Date().toISOString();

          return new Date(timestampB).getTime() - new Date(timestampA).getTime();
        });

        setResults(sortedResults);
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    // Get query from URL
    const urlQuery = getInitialQuery();
    
    if (urlQuery) {
      setSearchQuery(urlQuery);
      setHasSearched(true);
      fetchSearchResults(urlQuery);
      // Always reset selected device when loading from URL
      setSelectedDevice(null);
    } else {
      fetchInitialResults();
    }
  }, [location.search, getInitialQuery, parseMetadata]); // Re-run when URL search params change

  const handleItemClick = (item: any) => {
    console.log("Item clicked:", item);
    if (item.metadata) {
      const metadata = item.metadata.parsed || {};
      
      // Try to get the blockchain hash as the unique identifier
      let identifier = null;
      
      // First try to get it from blockchainData
      if (metadata.blockchainData && metadata.blockchainData.hash) {
        identifier = metadata.blockchainData.hash;
        console.log("Using blockchain hash as identifier:", identifier);
      } 
      // Fallback to other identifiers if blockchain hash is not available
      else {
        const deviceInfo = metadata.deviceInfo || {};
        identifier = 
          item.metadata.identifier ||
          metadata.serialNumber ||
          deviceInfo.serialNumber ||
          metadata.serial ||
          deviceInfo.serial ||
          metadata.identifier ||
          deviceInfo.identifier ||
          null;
        console.log("Using fallback identifier:", identifier);
      }

      if (identifier) {
        setSelectedDevice(identifier);
      } else {
        console.log("NO UNIQUE IDENTIFIER FOUND", metadata);
      }
    }
  };

  // Get device details for the selected device
  const getDeviceDetails = (): DeviceDetails | null => {
    if (!selectedDevice || results.length === 0) return null;

    const deviceRecords = results.filter((result: SearchResult) => {
      const metadata = parseMetadata(result.metadata_raw);
      
      // Use blockchain hash as the unique identifier
      const blockchainHash = metadata.blockchainData?.hash;
      
      console.log("Filtering by hash:", blockchainHash, "Selected device:", selectedDevice);
      console.log("Metadata", result.metadata_raw);
      
      // Compare the blockchain hash with the selected device identifier
      return blockchainHash === selectedDevice;
    });

    if (deviceRecords.length === 0) return null;

    const latestRecord = deviceRecords[0];
    const metadata = parseMetadata(latestRecord.metadata_raw);
    const deviceInfo = metadata.deviceInfo || {};

    // Get title from various possible locations
    const title =
      metadata.title ||
      deviceInfo.title ||
      metadata.name ||
      deviceInfo.name ||
      "Unknown Device";

    return {
      identifier: selectedDevice,
      title,
      recordCount: deviceRecords.length,
      rawMetadata: metadata,
    };
  };

  return {
    searchQuery,
    results,
    loading,
    hasSearched,
    selectedDevice,
    parseMetadata,
    handleSearch,
    handleItemClick,
    getDeviceDetails,
    setSelectedDevice,
  };
};
